import {
  CargaCapacidade,
  ConfiabilidadePrazo,
  EvolucaoCarteira,
  IndicadoresRelatorio,
  IParqueDeMaquinasRelatorio,
  IPessoasPorCelulaRelatorio,
  MudancasCarteiraRelatorio,
} from '@/domain/relatorios/interfaces/relatorios';
import { relatoriosRepository } from '@/domain/relatorios/repositories/relatoriosRepository';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { createStore } from '@/lib/zustand';

/** Horizon of the load vs capacity report: the eight weeks programming can still move. */
export const SEMANAS_CARGA = 8;

interface RelatoriosState {
  evolucao: EvolucaoCarteira | null;
  loading: boolean;
  failed: boolean;
  /** API message of the last failed load; null while nothing failed. */
  erro: string | null;
  /** Applied period. Empty means "let the API resolve its default window". */
  inicio: string;
  fim: string;
  loadEvolucao: () => Promise<void>;
  aplicarPeriodo: (inicio: string, fim: string) => Promise<void>;
  limparPeriodo: () => Promise<void>;

  /** Um retrato de quem esta em cada celula; carrega uma vez por sessao. */
  pessoas: IPessoasPorCelulaRelatorio | null;
  pessoasLoading: boolean;
  pessoasFailed: boolean;
  loadPessoas: () => Promise<void>;
  /** Botao de tentar de novo: sempre volta para a API. */
  recarregarPessoas: () => Promise<void>;

  parque: IParqueDeMaquinasRelatorio | null;
  parqueLoading: boolean;
  parqueFailed: boolean;
  loadParque: () => Promise<void>;
  recarregarParque: () => Promise<void>;

  mudancas: MudancasCarteiraRelatorio | null;
  mudancasLoading: boolean;
  mudancasFailed: boolean;
  mudancasErro: string | null;
  /** Loads once per session; a tab switch reads what is already here. */
  loadMudancas: () => Promise<void>;
  /** Retry button: always goes back to the API. */
  /**
   * As duas pontas da comparação. Sem nenhuma, hoje contra a fotografia anterior, que
   * é a leitura de todo dia; com as duas, o par que a pessoa escolheu.
   */
  recarregarMudancas: (dia?: string, base?: string) => Promise<void>;

  carga: CargaCapacidade | null;
  cargaLoading: boolean;
  cargaFailed: boolean;
  cargaErro: string | null;
  loadCarga: () => Promise<void>;
  recarregarCarga: () => Promise<void>;

  indicadores: IndicadoresRelatorio | null;
  indicadoresLoading: boolean;
  indicadoresFailed: boolean;
  indicadoresErro: string | null;
  /** Applied period of the indicators. Empty lets the API resolve its default window. */
  indicadoresInicio: string;
  indicadoresFim: string;
  /** Loads once per session; a tab switch reads what is already here. */
  loadIndicadores: () => Promise<void>;
  /** Retry button: always goes back to the API. */
  recarregarIndicadores: () => Promise<void>;
  /** The three indicators recalculated over the chosen period. */
  aplicarPeriodoIndicadores: (inicio: string, fim: string) => Promise<void>;
  limparPeriodoIndicadores: () => Promise<void>;

  confiabilidade: ConfiabilidadePrazo | null;
  confiabilidadeLoading: boolean;
  confiabilidadeFailed: boolean;
  confiabilidadeErro: string | null;
  loadConfiabilidade: () => Promise<void>;
  recarregarConfiabilidade: () => Promise<void>;
}

// Changing the dates fires one load each; only the latest answer may paint the report.
let loadSequence = 0;
let indicadoresSequence = 0;
/** "O que mudou": a period applied twice in a row must show the answer of the LAST one. */
let mudancasSequence = 0;

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export const relatoriosStore = createStore<RelatoriosState>((set, get) => ({
  evolucao: null,
  loading: false,
  failed: false,
  erro: null,
  inicio: '',
  fim: '',

  loadEvolucao: async () => {
    loadSequence += 1;
    const sequence = loadSequence;
    set({ loading: true });
    try {
      const { inicio, fim } = get();
      const evolucao = await relatoriosRepository.evolucaoCarteira({ inicio, fim });
      if (sequence === loadSequence) {
        set({ evolucao, failed: false, erro: null });
      }
    } catch (error) {
      logger.error('[relatoriosStore] failed to load carteira evolution', error);
      if (sequence === loadSequence) {
        set({ failed: true, erro: messageOf(error) });
      }
    } finally {
      if (sequence === loadSequence) {
        set({ loading: false });
      }
    }
  },

  aplicarPeriodo: async (inicio, fim) => {
    set({ inicio, fim });
    await get().loadEvolucao();
  },

  /** Back to the API's default window, which is what the report opens with. */
  limparPeriodo: async () => {
    set({ inicio: '', fim: '' });
    await get().loadEvolucao();
  },

  mudancas: null,
  mudancasLoading: false,
  mudancasFailed: false,
  mudancasErro: null,

  /**
   * The tab panel mounts on first activation and unmounts when someone leaves it, so
   * this skips the request when the report is already in hand. A failed load is NOT
   * retried on its own either: the error state offers the button, like everywhere else.
   */
  loadMudancas: async () => {
    const { mudancas, mudancasFailed, mudancasLoading } = get();
    if (mudancas !== null || mudancasFailed || mudancasLoading) {
      return;
    }
    await get().recarregarMudancas();
  },

  recarregarMudancas: async (dia?: string, base?: string) => {
    mudancasSequence += 1;
    const sequence = mudancasSequence;
    set({ mudancasLoading: true });
    try {
      const mudancas = await relatoriosRepository.mudancasCarteira(dia, base);
      if (sequence === mudancasSequence) {
        set({ mudancas, mudancasFailed: false, mudancasErro: null });
      }
    } catch (error) {
      logger.error('[relatoriosStore] failed to load carteira changes', error);
      if (sequence === mudancasSequence) {
        set({ mudancasFailed: true, mudancasErro: messageOf(error) });
      }
    } finally {
      if (sequence === mudancasSequence) {
        set({ mudancasLoading: false });
      }
    }
  },

  carga: null,
  cargaLoading: false,
  cargaFailed: false,
  cargaErro: null,

  loadCarga: async () => {
    const { carga, cargaFailed, cargaLoading } = get();
    if (carga !== null || cargaFailed || cargaLoading) {
      return;
    }
    await get().recarregarCarga();
  },

  recarregarCarga: async () => {
    set({ cargaLoading: true });
    try {
      const carga = await relatoriosRepository.cargaCapacidade(SEMANAS_CARGA);
      set({ carga, cargaFailed: false, cargaErro: null });
    } catch (error) {
      logger.error('[relatoriosStore] failed to load week load vs capacity', error);
      set({ cargaFailed: true, cargaErro: messageOf(error) });
    } finally {
      set({ cargaLoading: false });
    }
  },

  indicadores: null,
  indicadoresLoading: false,
  indicadoresFailed: false,
  indicadoresErro: null,
  indicadoresInicio: '',
  indicadoresFim: '',

  loadPessoas: async () => {
    const { pessoas, pessoasFailed, pessoasLoading } = get();
    if (pessoas !== null || pessoasFailed || pessoasLoading) {
      return;
    }
    await get().recarregarPessoas();
  },

  recarregarPessoas: async () => {
    set({ pessoasLoading: true });
    try {
      set({ pessoas: await relatoriosRepository.pessoasPorCelula(), pessoasFailed: false });
    } catch (error) {
      logger.error('[relatoriosStore] failed to load pessoas por celula', error);
      set({ pessoasFailed: true });
    } finally {
      set({ pessoasLoading: false });
    }
  },

  loadParque: async () => {
    const { parque, parqueFailed, parqueLoading } = get();
    if (parque !== null || parqueFailed || parqueLoading) {
      return;
    }
    await get().recarregarParque();
  },

  recarregarParque: async () => {
    set({ parqueLoading: true });
    try {
      set({ parque: await relatoriosRepository.parqueDeMaquinas(), parqueFailed: false });
    } catch (error) {
      logger.error('[relatoriosStore] failed to load parque de maquinas', error);
      set({ parqueFailed: true });
    } finally {
      set({ parqueLoading: false });
    }
  },

  loadIndicadores: async () => {
    const { indicadores, indicadoresFailed, indicadoresLoading } = get();
    if (indicadores !== null || indicadoresFailed || indicadoresLoading) {
      return;
    }
    await get().recarregarIndicadores();
  },

  // Guarda de sequencia propria: trocar o periodo depressa dispara um load por clique, e
  // a resposta lenta de antes chegaria DEPOIS da certa e pintaria o periodo errado.
  recarregarIndicadores: async () => {
    indicadoresSequence += 1;
    const sequence = indicadoresSequence;
    set({ indicadoresLoading: true });
    try {
      const { indicadoresInicio, indicadoresFim } = get();
      const indicadores = await relatoriosRepository.indicadores({
        inicio: indicadoresInicio,
        fim: indicadoresFim,
      });
      if (sequence === indicadoresSequence) {
        set({ indicadores, indicadoresFailed: false, indicadoresErro: null });
      }
    } catch (error) {
      logger.error('[relatoriosStore] failed to load PRD indicators', error);
      if (sequence === indicadoresSequence) {
        set({ indicadoresFailed: true, indicadoresErro: messageOf(error) });
      }
    } finally {
      if (sequence === indicadoresSequence) {
        set({ indicadoresLoading: false });
      }
    }
  },

  aplicarPeriodoIndicadores: async (inicio, fim) => {
    set({ indicadoresInicio: inicio, indicadoresFim: fim });
    await get().recarregarIndicadores();
  },

  /** Back to the API's default window, which is what the report opens with. */
  limparPeriodoIndicadores: async () => {
    set({ indicadoresInicio: '', indicadoresFim: '' });
    await get().recarregarIndicadores();
  },

  pessoas: null,
  pessoasLoading: false,
  pessoasFailed: false,

  parque: null,
  parqueLoading: false,
  parqueFailed: false,

  confiabilidade: null,
  confiabilidadeLoading: false,
  confiabilidadeFailed: false,
  confiabilidadeErro: null,

  loadConfiabilidade: async () => {
    const { confiabilidade, confiabilidadeFailed, confiabilidadeLoading } = get();
    if (confiabilidade !== null || confiabilidadeFailed || confiabilidadeLoading) {
      return;
    }
    await get().recarregarConfiabilidade();
  },

  recarregarConfiabilidade: async () => {
    set({ confiabilidadeLoading: true });
    try {
      const confiabilidade = await relatoriosRepository.confiabilidadePrazo();
      set({ confiabilidade, confiabilidadeFailed: false, confiabilidadeErro: null });
    } catch (error) {
      logger.error('[relatoriosStore] failed to load deadline reliability', error);
      set({ confiabilidadeFailed: true, confiabilidadeErro: messageOf(error) });
    } finally {
      set({ confiabilidadeLoading: false });
    }
  },
}));
